import Link from "next/link";
import React, { useCallback } from "react";
import LanguageDropdown from "./LanguageDropdown";
import { NAV_ITEMS, NavLabelKey } from "../constants";
import { useTranslations } from "next-intl";
import { useRouter } from "next/navigation";

const DesktopTopBar = ({
  desktopPillStyle,
  isDenseLocale,
  phase,
  showFullBar,
  handleLogoClick,
  scrollToSection,
  navButtonClass,
  setIsLangOpen,
  isLangOpen,
  currentLocale,
  switchLanguage,
  hasUserId,
  pastTransactionsHref,
  isPastTransactionsPage,
  userDisplayName,
  handleLogout,
  setIsMenuOpen,
  isMenuOpen,
  setLoginOpen,
  activeSection,
  isProfileOpen,
  setIsProfileOpen,
  onDeleteAccount,
  isPremiumUser,
}: {
  desktopPillStyle: () => React.CSSProperties;
  isDenseLocale: boolean;
  phase: number;
  showFullBar: boolean;
  handleLogoClick: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  scrollToSection: (
    sectionId: string,
    labelKey:
      | "productCatalog"
      | "aboutUs"
      | "ourVision"
      | "ourMission"
      | "contactUs",
    shouldCloseMobileNav?: boolean | undefined,
  ) => void;
  navButtonClass: (
    labelKey:
      | "productCatalog"
      | "aboutUs"
      | "ourVision"
      | "ourMission"
      | "contactUs",
  ) => string;
  setIsLangOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isLangOpen: boolean;
  currentLocale: string;
  switchLanguage: (
    locale:
      "as" | "en" | "hi" | "mr" | "bn" | "br" | "do" | "gu" | "kn" | "ks" | "ko" | "mai" | "ml"
      | "mni" | "ne" | "or" | "pa" | "sd" | "ta" | "te",
  ) => void;
  hasUserId: boolean;
  pastTransactionsHref: string;
  isPastTransactionsPage: boolean;
  userDisplayName: string;
  handleLogout: () => void;
  setIsMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isMenuOpen: boolean;
  setLoginOpen: React.Dispatch<React.SetStateAction<boolean>>;
  activeSection: NavLabelKey | null;
  isProfileOpen: boolean;
  setIsProfileOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onDeleteAccount: () => void;
  isPremiumUser: boolean;
}) => {
  const t = useTranslations("topbar");
  const router = useRouter();

  const handlePastTransactionsClick = useCallback(() => {
    const anyOpen = isLangOpen || isMenuOpen;
    if (anyOpen) {
      setIsLangOpen(false);
      setIsMenuOpen(false);
      window.setTimeout(() => router.push(pastTransactionsHref), 220);
    } else {
      router.push(pastTransactionsHref);
    }
  }, [isLangOpen, isMenuOpen, setIsLangOpen, setIsMenuOpen, router, pastTransactionsHref]);

  return (
    <div
      className={`hidden lg:flex topbar-full items-center relative pointer-events-auto bg-white shadow-lg min-h-14 w-full max-w-[96rem]${isDenseLocale ? " topbar-dense-locale" : ""}`}
      style={desktopPillStyle()}
    >
      <div
        className="absolute inset-0 flex items-center justify-center aspect-square rounded-full overflow-hidden transition-opacity duration-300"
        style={{
          opacity: phase < 2 ? 1 : 0,
          pointerEvents: phase < 2 ? "auto" : "none",
          height: "100%",
          width: "100%",
        }}
      >
        <Link
          href="/"
          onClick={handleLogoClick}
          className="w-full h-full flex items-center justify-center"
        >
          <img
            src="/logo/circular_logo.svg"
            alt="AgarMart"
            className="w-full h-full object-contain"
          />
        </Link>
      </div>

      <div
        className={`flex items-center w-full font-bold transition-opacity duration-300 justify-between ${isDenseLocale ? "px-3 gap-1 xl:px-5 xl:gap-2" : "px-5 gap-2"
          }`}
        style={{
          opacity: showFullBar ? 1 : 0,
          pointerEvents: showFullBar ? "auto" : "none",
        }}
      >
        <Link href="/" onClick={handleLogoClick} className="shrink-0">
          <img
            src="/logo/agar_mart_logo.svg"
            alt="AgarMart Logo"
            className={`topbar-logo w-auto ${isDenseLocale ? "h-10 xl:h-12" : "h-14"}`}
          />
        </Link>

        <div
          className={`topbar-nav hidden xl:flex items-center flex-1 justify-center min-w-0 ${isDenseLocale ? "gap-1 lg:gap-1.5" : "gap-0.5 lg:gap-1"
            }`}
        >
          {NAV_ITEMS.map(({ labelKey, sectionId }) => (
            <button
              key={labelKey}
              type="button"
              onClick={() => scrollToSection(sectionId, labelKey)}
              className={navButtonClass(labelKey)}
            >
              {t(labelKey)}
            </button>
          ))}
        </div>

        <div
          className={`flex items-center justify-end shrink-0 ${isDenseLocale ? "gap-1 xl:gap-1.5" : "gap-1.5 lg:gap-2.5"
            }`}
        >
          <div data-lang-dropdown className="relative shrink-0">
            <button
              type="button"
              onClick={() => { setIsLangOpen((value) => !value); setIsMenuOpen(false); }}
              className="language-button flex items-center justify-center gap-2 h-10 w-10 xl:w-54 xl:px-4 px-0 cursor-pointer text-white rounded-full bg-[#0A2540] transition-all duration-200"
            >
              <img
                src="/logo/globe_logo.svg"
                alt={t("changeLanguage")}
                className="h-4 w-4 shrink-0"
              />
              <span className="language-button-text hidden xl:inline flex-1 text-sm xl:text-base text-left whitespace-nowrap overflow-hidden">
                {t("changeLanguage")}
              </span>
              <img
                src="/logo/dropdown_logo.svg"
                alt={t("dropdown")}
                className={`hidden xl:block h-2 w-2 shrink-0 transition-transform duration-200 ${isLangOpen ? "rotate-180" : ""}`}
              />
            </button>

            <div className="absolute top-full left-0 w-full h-2" />
            <div
              className={`absolute left-0 w-52 z-99999 transition-all duration-200 ${isLangOpen
                ? "opacity-100 pointer-events-auto translate-y-0"
                : "opacity-0 pointer-events-none -translate-y-1"
                }`}
              style={{ top: "calc(100% + 8px)" }}
            >
              <LanguageDropdown
                currentLocale={currentLocale}
                onSwitch={switchLanguage}
                t={t}
              />
            </div>
          </div>

          {hasUserId && (
            <div className="relative group">
              <button
                type="button"
                onClick={handlePastTransactionsClick}
                className={`flex items-center justify-center h-9 w-9 rounded-full shrink-0 cursor-pointer transition-colors duration-150 ${isPastTransactionsPage ? "text-[#117DD4]" : "text-[#0A2540]"
                  }`}
                style={{ backgroundColor: "rgba(17, 125, 212, 0.08)" }}
                aria-label={t("pastTransactions")}
              >
                <svg
                  className="h-5 w-5"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 8v4l3 3M3.22302 14C4.13247 18.008 7.71683 21 12 21c4.9706 0 9-4.0294 9-9 0-4.97056-4.0294-9-9-9-3.72916 0-6.92858 2.26806-8.29409 5.5M7 9H3V5"
                  />
                </svg>
              </button>
              <span className="absolute left-1/2 -translate-x-1/2 top-full mt-2 whitespace-nowrap rounded bg-gray-800 text-white text-xs px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                {t("pastTransactions")}
              </span>
            </div>
          )}

          {hasUserId && (
            <div
              className="relative hidden xl:inline-block"
              data-profile-button
            >
              <button
                type="button"
                onClick={() => setIsProfileOpen((value) => !value)}
                className={`topbar-profile flex items-center rounded-3xl border border-amber-200 bg-amber-50 shadow-sm text-[#0A2540] cursor-pointer hover:bg-amber-100 transition-colors duration-150 ${isDenseLocale
                  ? "gap-1.5 px-1.5 py-1 xl:gap-2 xl:px-2 xl:py-1.5 text-xs xl:text-sm"
                  : "gap-2 px-2 py-1.5 text-sm"
                  }`}
              >
                <span
                  className={`flex items-center justify-center rounded-full bg-[#0A2540] text-white shadow-sm shrink-0 ${isDenseLocale ? "h-7 w-7 xl:h-8 xl:w-8" : "h-8 w-8"
                    }`}
                >
                  <svg
                    className="h-4 w-4"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4z" />
                    <path d="M4 20c0-2.21 1.79-4 4-4h8c2.21 0 4 1.79 4 4" />
                  </svg>
                </span>
                <div className="min-w-0 text-left">
                  <div className="text-sm font-semibold text-center text-[#0A2540] whitespace-nowrap">
                    {userDisplayName}
                  </div>
                  <div className="topbar-profile-welcome text-[11px] text-slate-500 whitespace-nowrap">
                    {isPremiumUser ? (
                      <span
                        className="inline-flex items-center gap-0.5 rounded-full border border-amber-300 bg-linear-to-r from-amber-400 to-yellow-300 px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-[0.1em] text-[#5b3a00] shadow-sm shrink-0"
                        aria-label={t("premiumBadgeAria")}
                        title={t("premiumBadge")}
                      >
                        <svg
                          className="h-2.5 w-2.5"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          aria-hidden="true"
                        >
                          <path d="M10 1.75l2.15 4.36 4.81.7-3.48 3.39.82 4.79L10 12.73 5.7 14.99l.82-4.79-3.48-3.39 4.81-.7L10 1.75Z" />
                        </svg>
                        {t("premiumBadge")}
                      </span>
                    ) : (
                      <>{t("welcome")}</>
                    )}
                  </div>
                </div>
              </button>

              <div
                data-profile-dropdown
                className={`absolute right-0 mt-2 w-48 rounded-xl bg-white border border-slate-200 shadow-lg z-99999 p-2 transition-all duration-200 ${isProfileOpen
                  ? "opacity-100 pointer-events-auto scale-100"
                  : "opacity-0 pointer-events-none scale-95"
                  }`}
                style={{ transformOrigin: "top right" }}
              >
                <button
                  type="button"
                  onClick={() => {
                    onDeleteAccount();
                    setIsProfileOpen(false);
                  }}
                  className="w-full text-left rounded-lg px-3 py-2 text-sm font-semibold text-red-500 hover:bg-red-50 transition-colors duration-150"
                >
                  {t("deleteAccount")}
                </button>
              </div>
            </div>
          )}

          {hasUserId && (
            <button
              type="button"
              className={`topbar-logout flex items-center gap-1.5 whitespace-nowrap text-red-500 font-semibold hover:bg-red-50 py-2 rounded transition-colors cursor-pointer shrink-0 ${isDenseLocale ? "px-1.5 xl:px-3" : "px-2 lg:px-4 gap-2"
                }`}
              onClick={handleLogout}
            >
              <svg
                className="h-5 w-5 text-red-500 shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                />
              </svg>
              <span
                className={`topbar-logout-text ${isDenseLocale ? "hidden xl:inline" : "hidden lg:inline"}`}
              >
                {t("profileLogout")}
              </span>
            </button>
          )}

          {!hasUserId && (
            <button
              type="button"
              className="flex items-center gap-2 text-[#117DD4] font-semibold hover:bg-blue-50 px-2 lg:px-4 py-2 rounded transition-colors cursor-pointer"
              onClick={() => setLoginOpen(true)}
            >
              <svg
                className="h-5 w-5 text-[#117DD4]"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 3h4a2 2 0 012 2v14a2 2 0 01-2 2h-4M10 17l5-5m0 0l-5-5m5 5H3"
                />
              </svg>
              <span className="hidden lg:inline">{t("profileLogin")}</span>
            </button>
          )}

          <div className="relative shrink-0 xl:hidden" data-menu-button>
            <button
              type="button"
              className="flex items-center justify-center h-10 w-10 rounded-full bg-[#0A2540] text-white transition-colors duration-200 hover:bg-slate-900"
              onClick={() => { setIsMenuOpen((value) => !value); setIsLangOpen(false); }}
            >
              {isMenuOpen ? (
                <svg
                  className="h-5 w-5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M6 6l12 12M6 18L18 6" />
                </svg>
              ) : (
                <svg
                  className="h-5 w-5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>

            <div
              data-menu-dropdown
              className={`absolute right-0 mt-2 w-64 rounded-xl bg-white border border-slate-200 shadow-lg z-99999 transition-all duration-200 ${isMenuOpen ? "opacity-100 pointer-events-auto scale-100" : "opacity-0 pointer-events-none scale-95"}`}
              style={{ transformOrigin: "top right" }}
            >
              <div className="flex flex-col p-2 pb-0">
                {hasUserId && (
                  <div className="px-4 pb-2 mb-2 rounded-3xl border border-amber-200 bg-amber-50 shadow-sm">
                    <div className="flex items-center gap-3 p-3">
                      <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#0A2540] text-white shadow-sm">
                        <svg
                          className="h-5 w-5"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4z" />
                          <path d="M4 20c0-2.21 1.79-4 4-4h8c2.21 0 4 1.79 4 4" />
                        </svg>
                      </div>
                      <div className="min-w-0">
                        <div className="truncate text-base text-center font-semibold text-[#0A2540]">
                          {userDisplayName}
                        </div>
                        <div className="truncate text-xs text-slate-500">
                          {isPremiumUser ? (
                            <span
                              className="inline-flex items-center gap-0.5 rounded-full border border-amber-300 bg-linear-to-r from-amber-400 to-yellow-300 px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-widest text-[#5b3a00] shadow-sm shrink-0"
                              aria-label={t("premiumBadgeAria")}
                              title={t("premiumBadge")}
                            >
                              <svg
                                className="h-2.5 w-2.5"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                                aria-hidden="true"
                              >
                                <path d="M10 1.75l2.15 4.36 4.81.7-3.48 3.39.82 4.79L10 12.73 5.7 14.99l.82-4.79-3.48-3.39 4.81-.7L10 1.75Z" />
                              </svg>
                              {t("premiumBadge")}
                            </span>
                          ) : (
                            <>{t("welcome")}</>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {NAV_ITEMS.map(({ labelKey, sectionId }) => (
                  <button
                    key={labelKey}
                    type="button"
                    onClick={() => {
                      scrollToSection(sectionId, labelKey);
                      setIsMenuOpen(false);
                    }}
                    className={`w-full text-left rounded-2xl px-4 py-3 text-sm transition-colors duration-150 ${activeSection === labelKey
                      ? "bg-[#0A2540] text-white"
                      : "text-slate-700 hover:bg-slate-100"
                      }`}
                  >
                    {t(labelKey)}
                  </button>
                ))}
                {hasUserId && (
                  <button
                    type="button"
                    onClick={() => {
                      onDeleteAccount();
                      setIsMenuOpen(false);
                    }}
                    className="w-full text-left rounded-2xl px-4 py-3 text-sm transition-colors duration-150 text-red-500 hover:bg-red-50 font-semibold mb-2"
                  >
                    {t("deleteAccount")}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DesktopTopBar;
